/**
 * Sound preset select, preview button, volume slider, and custom-file row for
 * one configurable sound. Owns the custom-file verification and the fallback
 * when the platform has no custom sound picker.
 */

import { useEffect, useMemo, useState, type ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { usePlatformCapabilities, usePlatformFeatures } from '../config';
import {
  BUILTIN_NOTIFICATION_SOUNDS,
  DEFAULT_BUILTIN_NOTIFICATION_SOUND_ID,
} from '../constants/builtinNotificationSounds';
import {
  MAX_NOTIFICATION_GAIN,
  type NotificationSoundId,
} from '../constants/notificationSoundPreferenceShared';
import { useClaimAchievement } from '../hooks/useClaimAchievement';
import { invalidateNotificationSoundCustomCache, previewNotificationSound } from '../utils/notificationSound';
import { Alert } from './Alert';
import { NotificationSoundSelect } from './NotificationSoundSelect';
import { Tooltip } from './Tooltip';

const MAX_PERCENT = Math.round(MAX_NOTIFICATION_GAIN * 100);

function basenameFromPath(p: string): string {
  const parts = p.split(/[/\\]/);
  return parts[parts.length - 1] ?? p;
}

export interface SoundPickerFieldsLabels {
  select: string;
  preview: string;
  volume: string;
  volumeHint?: string;
  customFile: string;
  browse: string;
  fileMissing: string;
}

export interface SoundPickerFieldsProps {
  /** Unique per page; prefixes the select label and slider ids. */
  idPrefix: string;
  soundId: NotificationSoundId;
  customPath: string | null;
  volume: number;
  disabled?: boolean;
  labels: SoundPickerFieldsLabels;
  onSoundIdChange: (soundId: NotificationSoundId) => void;
  /** Called with the picked path; the caller should also select `custom`. */
  onCustomPathPicked: (path: string) => void;
  onVolumeChange: (gain: number) => void;
}

export function SoundPickerFields({
  idPrefix,
  soundId,
  customPath,
  volume,
  disabled = false,
  labels,
  onSoundIdChange,
  onCustomPathPicked,
  onVolumeChange,
}: SoundPickerFieldsProps) {
  const { t } = useTranslation();
  const { audio } = usePlatformCapabilities();
  const { hasCustomSoundPicker } = usePlatformFeatures();
  const claimAchievement = useClaimAchievement();
  const [browseBusy, setBrowseBusy] = useState(false);
  const [customMissing, setCustomMissing] = useState(false);

  const builtinItems = useMemo(
    () => BUILTIN_NOTIFICATION_SOUNDS.map((s) => ({ value: s.id, label: s.displayName })),
    []
  );
  const selectLabels = useMemo(
    () => ({
      none: t('account.settings.notifications.soundNone'),
      custom: t('account.settings.notifications.soundCustom'),
    }),
    [t]
  );

  useEffect(() => {
    if (!hasCustomSoundPicker && soundId === 'custom') {
      onSoundIdChange(DEFAULT_BUILTIN_NOTIFICATION_SOUND_ID);
    }
  }, [hasCustomSoundPicker, soundId, onSoundIdChange]);

  useEffect(() => {
    let cancelled = false;
    async function verifyCustom(): Promise<void> {
      if (soundId !== 'custom' || !customPath || !audio?.loadSoundFromPath) {
        setCustomMissing(false);
        return;
      }
      const buf = await audio.loadSoundFromPath(customPath);
      if (!cancelled) setCustomMissing(!buf || buf.byteLength === 0);
    }
    void verifyCustom();
    return () => {
      cancelled = true;
    };
  }, [soundId, customPath, audio]);

  const handleSoundIdChange = (value: string) => {
    const id = value as NotificationSoundId;
    onSoundIdChange(id);
    if (id !== 'custom') invalidateNotificationSoundCustomCache();
  };

  const handleBrowse = async () => {
    if (!audio?.pickSoundFile) return;
    setBrowseBusy(true);
    try {
      const picked = await audio.pickSoundFile();
      if (!picked) return;
      onCustomPathPicked(picked.path);
      invalidateNotificationSoundCustomCache();
      setCustomMissing(false);
    } finally {
      setBrowseBusy(false);
    }
  };

  const handleVolumeChange = (e: ChangeEvent<HTMLInputElement>) => {
    const pct = Number(e.target.value);
    onVolumeChange(pct / 100);
    if (pct >= MAX_PERCENT) claimAchievement('notification_volume_maxed');
  };

  const percent = Math.round(volume * 100);
  const selectLabelId = `${idPrefix}-sound-preset-label`;
  const volumeId = `${idPrefix}-sound-volume`;

  return (
    <>
      <div className="app-settings-sound-row">
        <div id={selectLabelId} className="app-settings-sound-select-label">
          {labels.select}
        </div>
        <div className="app-settings-sound-row-controls">
          <NotificationSoundSelect
            value={soundId}
            disabled={disabled}
            hasCustomSoundPicker={hasCustomSoundPicker}
            builtinItems={builtinItems}
            labels={selectLabels}
            onValueChange={handleSoundIdChange}
            labelId={selectLabelId}
          />
          <button
            type="button"
            className="btn btn-secondary app-settings-sound-preview"
            disabled={
              disabled ||
              soundId === 'none' ||
              (soundId === 'custom' && (!customPath || !audio?.loadSoundFromPath))
            }
            onClick={() =>
              void previewNotificationSound({
                soundId,
                customPath,
                loadCustomSound: audio?.loadSoundFromPath,
                volume,
              })
            }
          >
            {labels.preview}
          </button>
        </div>
      </div>

      <div className="app-settings-sound-volume">
        <label htmlFor={volumeId} className="app-settings-sound-volume-label">
          {labels.volume}
        </label>
        <div className="app-settings-sound-volume-row">
          <input
            id={volumeId}
            type="range"
            className="app-settings-sound-volume-slider"
            min={0}
            max={MAX_PERCENT}
            step={1}
            value={percent}
            disabled={disabled || soundId === 'none'}
            onChange={handleVolumeChange}
            aria-valuemin={0}
            aria-valuemax={MAX_PERCENT}
            aria-valuenow={percent}
            aria-valuetext={`${percent}%`}
          />
          <span className="app-settings-sound-volume-value" aria-hidden>
            {percent}%
          </span>
        </div>
        {labels.volumeHint && <p className="app-settings-sound-volume-hint">{labels.volumeHint}</p>}
      </div>

      {hasCustomSoundPicker && soundId === 'custom' && (
        <div className="app-settings-custom-sound">
          <span className="app-settings-custom-sound-label">{labels.customFile}</span>
          <div className="app-settings-custom-sound-row">
            <Tooltip content={customPath ?? ''}>
              <span className="app-settings-custom-sound-name">
                {customPath ? basenameFromPath(customPath) : '—'}
              </span>
            </Tooltip>
            <button
              type="button"
              className="btn btn-secondary"
              disabled={disabled || browseBusy}
              onClick={() => void handleBrowse()}
            >
              {labels.browse}
            </button>
          </div>
          {customMissing && (
            <Alert variant="warning" className="app-settings-alert">
              {labels.fileMissing}
            </Alert>
          )}
        </div>
      )}
    </>
  );
}
